import { equilibriumThrust, estimatedParams } from './model';
import type { ControllerConfig, PlantParams } from './types';

export interface ControllerInput {
  /** reference altitude r (absolute), m */
  r: number;
  /** measured altitude (absolute), m */
  ym: number;
}

export interface ControllerOutput {
  e: number;
  Tff: number;
  uP: number;
  uI: number;
  uD: number;
  ufb: number;
  Tcmd: number;
  T: number;
  sat: -1 | 0 | 1;
  I: number;
  vEst: number;
  sw: -1 | 0 | 1;
  switched: boolean;
}

export function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

/**
 * Feedforward thrust T_ff for the selected mode, computed from the controller's *estimated* parameters.
 */
export function feedforward(cfg: ControllerConfig, plant: PlantParams, z0: number, r: number): number {
  const est = estimatedParams(plant, cfg);
  switch (cfg.ffMode) {
    case 'off':
      return 0;
    case 'operating-point':
      return equilibriumThrust(est, z0);
    case 'target-equilibrium':
      return est.m * est.g + est.k * (r - est.zs);
  }
}

/**
 * Discrete controller implementing T_cmd = T_ff + u_fb and T = clamp(T_cmd, 0, T_max).
 * Holds its own memory (integral, derivative filter, bang-bang switch) - separate from the plant state.
 */
export class Controller {
  private I = 0;
  private vEst = 0;
  private prevYm: number | null = null;
  private high: boolean;

  constructor(
    private cfg: ControllerConfig,
    private readonly plant: PlantParams,
    private readonly z0: number,
    private readonly dt: number,
  ) {
    this.high = cfg.bangInitial === 'high';
  }

  reset(): void {
    this.I = 0;
    this.vEst = 0;
    this.prevYm = null;
    this.high = this.cfg.bangInitial === 'high';
  }

  /** Live-adjustable input (manual thrust); does not reset memory. */
  setManualInput(value: number): void {
    this.cfg = { ...this.cfg, manualInput: value };
  }

  get manualInput(): number {
    return this.cfg.manualInput;
  }

  step({ r, ym }: ControllerInput): ControllerOutput {
    const c = this.cfg;
    const dt = this.dt;
    const e = r - ym;
    const Tmax = this.plant.Tmax;

    // Velocity estimate from successive measurements, smoothed by a first-order low-pass filter:
    // vEst ← vEst + α (raw − vEst), α = Δt / (τ + Δt). With τ = 0 this is the raw difference.
    if (this.prevYm === null) {
      this.vEst = 0;
    } else {
      const raw = (ym - this.prevYm) / dt;
      const alpha = dt / (c.derivTau + dt);
      this.vEst += alpha * (raw - this.vEst);
    }
    this.prevYm = ym;

    const Tff = feedforward(c, this.plant, this.z0, r);
    let uP = 0;
    let uI = 0;
    let uD = 0;
    let ufb = 0;
    let sw: -1 | 0 | 1 = -1;
    let switched = false;

    switch (c.kind) {
      case 'manual':
        ufb = c.manualInput;
        break;
      case 'open-loop':
        ufb = 0;
        break;
      case 'bang-bang': {
        const wasHigh = this.high;
        // Hysteresis: switch high above +h, low below −h, otherwise retain (also at equality).
        if (e > c.hysteresis) this.high = true;
        else if (e < -c.hysteresis) this.high = false;
        switched = wasHigh !== this.high;
        sw = this.high ? 1 : 0;
        ufb = this.high ? c.bangDelta : -c.bangDelta;
        break;
      }
      case 'p':
      case 'pi':
      case 'pid': {
        uP = c.Kp * e;
        if (c.kind === 'pid') uD = -c.Kd * this.vEst;
        if (c.kind === 'pi' || c.kind === 'pid') {
          const before = Tff + uP + c.Ki * this.I + uD;
          const dI = e * dt;
          const push = c.Ki * dI;
          const windsFurther = (before >= Tmax && push > 0) || (before <= 0 && push < 0);
          if (!(c.antiWindup && windsFurther)) this.I += dI;
          uI = c.Ki * this.I;
        }
        ufb = uP + uI + uD;
        break;
      }
    }

    const Tcmd = Tff + ufb;
    const T = clamp(Tcmd, 0, Tmax);
    const sat: -1 | 0 | 1 = Tcmd > Tmax ? 1 : Tcmd < 0 ? -1 : 0;
    return { e, Tff, uP, uI, uD, ufb, Tcmd, T, sat, I: this.I, vEst: this.vEst, sw, switched };
  }
}

/** Actual bang-bang thrust levels after limiting, for display. */
export function bangLevels(cfg: ControllerConfig, plant: PlantParams, z0: number, r: number) {
  const Tff = feedforward(cfg, plant, z0, r);
  const highReq = Tff + cfg.bangDelta;
  const lowReq = Tff - cfg.bangDelta;
  return {
    highRequested: highReq,
    lowRequested: lowReq,
    high: clamp(highReq, 0, plant.Tmax),
    low: clamp(lowReq, 0, plant.Tmax),
  };
}
